import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { useFeatureFlag, useFeatureFlagsLoaded } from '../hooks/useFeatureFlag';

export type ThemeMode = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeModeContextType {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  resolvedTheme: ResolvedTheme;
}

const ThemeModeContext = createContext<ThemeModeContextType | undefined>(
  undefined,
);

// The theme picked in the account menu. index.html reads it too.
const STORAGE_KEY = 'todesktop-theme-mode';
// The last resolved systemTheme flag, so a reload doesn't flash dark before a
// "force light" override loads. index.html reads it too.
const SYSTEM_THEME_STORAGE_KEY = 'todesktop-system-theme';

const isThemeMode = (value: unknown): value is ThemeMode =>
  value === 'system' || value === 'light' || value === 'dark';

const readStoredMode = (): ThemeMode => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isThemeMode(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
};

const readStoredSystemTheme = (): boolean | undefined => {
  try {
    const stored = localStorage.getItem(SYSTEM_THEME_STORAGE_KEY);
    return stored === null ? undefined : stored === 'on';
  } catch {
    return undefined;
  }
};

export const ThemeModeProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  // The user's choice from the account menu; only applies while the
  // systemTheme flag is on (see below)
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() => {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }
    return 'light';
  });

  // Listen to system theme changes
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setSystemTheme(e.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const setMode = (newMode: ThemeMode) => {
    setModeState(newMode);
    try {
      localStorage.setItem(STORAGE_KEY, newMode);
    } catch {
      // Storage can be unavailable (private mode); the choice lasts the visit
    }
  };

  // Dark mode is behind a flag (on by default), so it can be switched off.
  // With it on, users pick system, light or dark (system by default); with it
  // off, everyone stays on light.
  const flagFollowsSystem = useFeatureFlag('systemTheme');
  const flagsLoaded = useFeatureFlagsLoaded();
  const [storedFollowsSystem] = useState(readStoredSystemTheme);
  const followsSystem = flagsLoaded
    ? flagFollowsSystem
    : (storedFollowsSystem ?? flagFollowsSystem);

  useEffect(() => {
    if (!flagsLoaded) return;
    try {
      localStorage.setItem(
        SYSTEM_THEME_STORAGE_KEY,
        flagFollowsSystem ? 'on' : 'off',
      );
    } catch {
      // Storage can be unavailable (private mode); the flag still applies
    }
  }, [flagsLoaded, flagFollowsSystem]);

  const effectiveMode: ThemeMode = followsSystem ? mode : 'light';
  const resolvedTheme: ResolvedTheme =
    effectiveMode === 'system' ? systemTheme : effectiveMode;

  // Apply theme class to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolvedTheme);
  }, [resolvedTheme]);

  const value = useMemo(
    () => ({ mode: effectiveMode, setMode, resolvedTheme }),
    [effectiveMode, resolvedTheme],
  );

  return (
    <ThemeModeContext.Provider value={value}>
      {children}
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within a ThemeModeProvider');
  }
  return context;
};

// Layout theme colors that adapt to light/dark mode
export const layoutColors = {
  light: {
    pageBg: '#fafafa',
    cardBg: '#ffffff',
    sidebarBg: 'transparent',
    navItemBg: 'transparent',
    navItemBgHover: '#f5f5f5',
    navItemBgActive: '#f5f5f5',
    navItemText: '#666',
    navItemTextHover: '#333',
    navItemTextActive: '#333',
    navItemIcon: '#444',
    navItemIconActive: '#3946f1',
    navSectionTitle: '#999',
    breadcrumbIconBg: '#f5f5f5',
    breadcrumbText: '#333',
    breadcrumbSeparator: '#ccc',
    userName: '#333',
    userEmail: '#666',
    statusColor: '#22c55e',
    externalChevron: '#999',
  },
  dark: {
    pageBg: '#1a1a1a',
    cardBg: '#2a2a2a',
    sidebarBg: 'transparent',
    navItemBg: 'transparent',
    navItemBgHover: '#333',
    navItemBgActive: '#333',
    navItemText: '#999',
    navItemTextHover: '#e0e0e0',
    navItemTextActive: '#e0e0e0',
    navItemIcon: '#888',
    navItemIconActive: '#6b7aff',
    navSectionTitle: '#666',
    breadcrumbIconBg: '#333',
    breadcrumbText: '#e0e0e0',
    breadcrumbSeparator: '#555',
    userName: '#e0e0e0',
    userEmail: '#999',
    statusColor: '#22c55e',
    externalChevron: '#666',
  },
};

export type LayoutColors = typeof layoutColors.light;
